import React, { useState } from "react";
import { GameButton, GameIcon, type GameIconName } from "./game";

/**
 * First-run walkthrough for the athlete app. A short, playful, slide-based
 * tour that introduces each screen so kids know what's where (and what will
 * be there once data arrives). Shown once, tracked in localStorage.
 */
export const ONBOARDING_FLAG = "igrowth_onboarding_seen_v1";

export function hasSeenOnboarding(): boolean {
  try {
    return localStorage.getItem(ONBOARDING_FLAG) === "1";
  } catch {
    return true; // storage disabled → don't nag
  }
}

type Slide = {
  icon: GameIconName;
  from: string;
  to: string;
  title: string;
  body: string;
};

const SLIDES: Slide[] = [
  {
    icon: "trophy",
    from: "from-indigo-500",
    to: "to-blue-600",
    title: "Bine ai venit în iGROWth!",
    body: "Învață, joacă-te și câștigă puncte, XP și insigne. Îți arătăm rapid unde găsești fiecare lucru.",
  },
  {
    icon: "star",
    from: "from-violet-500",
    to: "to-fuchsia-600",
    title: "Acasă",
    body: "Ecranul de start: vezi pe scurt punctele, nivelul tău și ce ai de făcut azi.",
  },
  {
    icon: "bulb",
    from: "from-emerald-500",
    to: "to-teal-600",
    title: "Quiz-uri",
    body: "Alege o categorie și rezolvă quiz-uri. Fiecare răspuns corect îți aduce puncte și te urcă în nivel.",
  },
  {
    icon: "flame",
    from: "from-amber-500",
    to: "to-orange-600",
    title: "Sesiuni & Evenimente",
    body: "Aici apar antrenamentele și evenimentele echipei tale. Dacă e gol acum, se vor umple în curând.",
  },
  {
    icon: "medal",
    from: "from-rose-500",
    to: "to-red-600",
    title: "Clasament",
    body: "Vezi cum stai față de colegi și urcă în top pe măsură ce aduni puncte.",
  },
  {
    icon: "bolt",
    from: "from-sky-500",
    to: "to-indigo-600",
    title: "Profilul tău",
    body: "Apasă pe numele tău din colțul din stânga sus ca să-ți vezi profilul, insignele și setările.",
  },
];

export default function OnboardingTour({ onDone }: { onDone: () => void }) {
  const [i, setI] = useState(0);
  const last = i === SLIDES.length - 1;
  const s = SLIDES[i];

  const finish = () => {
    try {
      localStorage.setItem(ONBOARDING_FLAG, "1");
    } catch {
      /* ignore */
    }
    onDone();
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center px-4 pt-4 pb-16 animate-fadeIn">
      <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden animate-zoom">
        {/* Illustration band */}
        <div className={`relative bg-gradient-to-br ${s.from} ${s.to} px-6 pt-8 pb-10 text-center`}>
          <button
            onClick={finish}
            className="absolute top-3 right-3 text-white/80 text-xs font-bold px-2 py-1 rounded-lg hover:bg-white/10"
          >
            Sari peste
          </button>
          <div className="w-20 h-20 mx-auto rounded-3xl bg-white/15 grid place-items-center ring-4 ring-white/20">
            <GameIcon name={s.icon} className="w-11 h-11 text-white drop-shadow" />
          </div>
        </div>

        <div className="px-6 pt-5 pb-6 text-center">
          <h2 className="text-xl font-extrabold text-slate-800">{s.title}</h2>
          <p className="text-sm text-slate-500 mt-2 leading-relaxed">{s.body}</p>

          {/* Progress dots */}
          <div className="flex items-center justify-center gap-1.5 mt-5">
            {SLIDES.map((_, idx) => (
              <span
                key={idx}
                className={`h-1.5 rounded-full transition-all ${
                  idx === i ? "w-5 bg-indigo-600" : "w-1.5 bg-slate-200"
                }`}
              />
            ))}
          </div>

          <div className="mt-5 flex items-center gap-3">
            {i > 0 && (
              <GameButton variant="ghost" onClick={() => setI(i - 1)} className="!w-auto px-5">
                Înapoi
              </GameButton>
            )}
            <GameButton onClick={() => (last ? finish() : setI(i + 1))} className="flex-1">
              {last ? "Hai să începem!" : "Continuă"}
            </GameButton>
          </div>
        </div>
      </div>
    </div>
  );
}
